import React, { useState } from 'react';
import styles from './App.module.scss';
import './styles/global.scss';
import useStore from './store';
import Header from './components/Header';
import WelcomeScreen from './components/WelcomeScreen';
import SetupScreen from './components/SetupScreen';
import PeopleScreen from './components/PeopleScreen';
import CalcScreen from './components/CalcScreen';
import StatsScreen from './components/StatsScreen';
import AlertModal from './components/AlertModal';
import ShareScreen from './components/ShareScreen';
import { ClipboardIcon, CheckIcon } from './icons/Icons';
import { encodeBanyaData, decodeBanyaData } from './utils';

function App() {
  const banya = useStore((s) => s.banya);
  const startBanya = useStore((s) => s.startBanya);
  const resetBanya = useStore((s) => s.resetBanya);

  const [activeTab, setActiveTab] = useState('people');
  const [showFinishAlert, setShowFinishAlert] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  // Режим создания новой бани (показываем SetupScreen без существующей бани)
  const [creatingNew, setCreatingNew] = useState(false);
  const [sharedData, setSharedData] = useState(null);
// Parse shared data from URL hash
  React.useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.startsWith('#/share/')) {
      const encoded = hash.slice(8); // Remove '#/share/'
      const data = decodeBanyaData(encoded);
      if (data) {
        setSharedData(data);
      }
    }
  }, []);

  const handleStartBanya = () => {
    setCreatingNew(true);
  };

  const handleSaveSetup = (startTime, endTime, hourlyRate, broomCost, name, date) => {
    if (!banya) {
      startBanya(startTime, endTime, hourlyRate, broomCost, name, date);
    }
    setCreatingNew(false);
    setActiveTab('people');
  };

  const handleFinishBanya = () => setShowFinishAlert(true);

  const confirmFinish = () => {
    resetBanya();
    setShowFinishAlert(false);
    setActiveTab('people');
    setCreatingNew(false);
  };

  // Есть данные по ссылке → share screen
  if (sharedData) {
    return <ShareScreen banya={sharedData} />;
  }

  // Нет бани и не создаём новую → welcome screen
  if (!banya && !creatingNew) {
    return (
      <div className={styles.app}>
        <Header banyaExists={false} />
        <WelcomeScreen onStart={handleStartBanya} />
      </div>
    );
  }

  // Нет бани, но создаём новую → setup screen
  if (!banya && creatingNew) {
    return (
      <div className={styles.app}>
        <Header banyaExists={false} />
        <SetupScreen onSave={handleSaveSetup} />
      </div>
    );
  }

  return (
    <div className={styles.app}>
      <Header
        banyaExists={true}
        activeTab={activeTab}
        onTabChange={(tab) => setActiveTab(tab)}
        onFinishBanya={handleFinishBanya}
        onShare={() => setShowShareModal(true)}
        banya={banya}
      />

      {activeTab === 'people' && <PeopleScreen />}
      {activeTab === 'stats' && <StatsScreen />}
      {activeTab === 'calc' && <CalcScreen />}
      {activeTab === 'settings' && (
        <SetupScreen
          onSave={handleSaveSetup}
          initialData={{
            name: banya.name,
            date: banya.date,
            startTime: banya.startTime,
            endTime: banya.endTime,
            hourlyRate: banya.hourlyRate,
            broomCost: banya.broomCost,
          }}
        />
      )}

      {showFinishAlert && (
        <AlertModal
          banya={banya}
          onYes={confirmFinish}
          onNo={() => setShowFinishAlert(false)}
        />
      )}

      {showShareModal && (
        <ShareModal banya={banya} onClose={() => setShowShareModal(false)} />
      )}
    </div>
  );
}

function ShareModal({ banya, onClose }) {
  const [copied, setCopied] = React.useState(false);

  const shareUrl = React.useMemo(() => {
    const encoded = encodeBanyaData(banya);
    if (!encoded) return '';
    return `${window.location.origin}${window.location.pathname}#/share/${encoded}`;
  }, [banya]);

  const handleCopy = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    }).catch(() => {
      prompt('Скопируйте ссылку:', shareUrl);
    });
  };

  return (
    <div className="alert-overlay" onClick={onClose}>
      <div className="alert-box" onClick={(e) => e.stopPropagation()}>
        <h3 className="alert-title">Ссылка на расчёт</h3>
        <div className="alert-share-wrap">
          <input
            type="text"
            value={shareUrl}
            readOnly
            onClick={(e) => e.target.select()}
            className="alert-share-input"
          />
          <button onClick={handleCopy} className="alert-copy-icon">
            {copied ? <CheckIcon color="#34c759" /> : <ClipboardIcon color="#8e8e93" />}
          </button>
        </div>
        <div className="alert-buttons-column">
          <button className="btn-cancel" onClick={onClose}>Закрыть</button>
        </div>
      </div>
    </div>
  );
}

export default App;
